import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { InlineUploader } from '../../InlineUploader/InlineUploader';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { logout } from '../../../redux/authentication/slice';
import { updateConfigOnboardingSettings, updateConfig } from '../../../redux/config/slice';

import { selectConfig, selectUpdateConfigLoading } from '../../../redux/config/selectors';
import { ALLOWED_FILE_TYPES, ONBOARDING_STEPS } from '../../../consts/consts';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingContentInfo,
  OnboardingStepWrapper,
  OnboardingContentGroupText,
  OnboardingDocumentSection
} from '../../ui/Layout/OnboardingLayout';
import { Heading2 } from '../../../components/ui/Typography/Heading';

const OnboardingStepDocumentsComponent = ({
  config,
  updateConfigLoading,
  updateConfig,
  updateConfigOnboardingSettings,
  logout
}) => {
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [privacyStatementValidationTriggered, setPrivacyStatementValidationTriggered] = useState(false);
  const [termsOfUseValidationTriggered, setTermsOfUseValidationTriggered] = useState(false);
  const [privacyStatement, setPrivacyStatement] = useState(null);
  const [termsOfUse, setTermsOfUse] = useState(null);

  const [privacyStatementSelection, setPrivacyStatementSelection] = useState('default');
  const [termsOfUseSelection, setTermsOfUseSelection] = useState('default');

  useEffect(() => {
    if (config) {
      const privacyStatementValue = config.privacy_statement || null;
      const termsOfUseValue = config.terms_of_use || null;

      setPrivacyStatement(privacyStatementValue);
      setTermsOfUse(termsOfUseValue);

      setPrivacyStatementSelection(privacyStatementValue ? 'custom' : 'default');
      setTermsOfUseSelection(termsOfUseValue ? 'custom' : 'default');

      setIsFieldsTouched(true);
    }
  }, [config]);

  const isFormValid = () => {
    const isPrivacyValid =
      privacyStatementSelection === 'default' || (privacyStatementSelection === 'custom' && privacyStatement !== null);
    const isTermsValid = termsOfUseSelection === 'default' || (termsOfUseSelection === 'custom' && termsOfUse !== null);

    return isPrivacyValid && isTermsValid;
  };

  const isFormDataEqual = () => {
    if (!isFieldsTouched) {
      return true;
    }

    const currentData = {
      privacy_statement: privacyStatement || null,
      terms_of_use: termsOfUse || null
    };

    const initialData = {
      privacy_statement: config?.privacy_statement || null,
      terms_of_use: config?.terms_of_use || null
    };

    return deepEqual(currentData, initialData);
  };

  const handleSaveConfig = async (callback) => {
    if (isFormDataEqual()) {
      callback?.();
      return;
    }

    updateConfig({
      config: {
        ...config,
        privacy_statement: privacyStatement || null,
        terms_of_use: termsOfUse || null
      },
      callback
    });
  };

  const handleOnBack = () => {
    if (!isFormValid()) {
      if (privacyStatementSelection === 'custom' && !privacyStatement) setPrivacyStatementValidationTriggered(true);
      if (termsOfUseSelection === 'custom' && !termsOfUse) setTermsOfUseValidationTriggered(true);
      return;
    }
    handleSaveConfig(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.USERS });
    });
  };

  const handleSaveAndLogout = () => {
    if (isFormValid() && !isFormDataEqual()) {
      handleSaveConfig(() => logout());
    } else {
      logout();
    }
  };

  const handleOnNext = () => {
    if (!isFormValid()) {
      if (privacyStatementSelection === 'custom' && !privacyStatement) setPrivacyStatementValidationTriggered(true);
      if (termsOfUseSelection === 'custom' && !termsOfUse) setTermsOfUseValidationTriggered(true);
      return;
    }
    handleSaveConfig(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.ORG_SUCCESS });
    });
  };

  const handlePrivacyStatementChange = (file) => {
    setPrivacyStatement(file);
    if (file) setPrivacyStatementSelection('custom');
    setIsFieldsTouched(Math.random());
  };

  const handlePrivacyStatementDelete = () => {
    setPrivacyStatement(null);
    setPrivacyStatementSelection('default');
    setIsFieldsTouched(Math.random());
  };

  const handlePrivacyStatementSelectionChange = (selection) => {
    setPrivacyStatementSelection(selection);
    if (selection !== 'custom') setPrivacyStatementValidationTriggered(false);
    setIsFieldsTouched(Math.random());
  };

  const handleTermsOfUseChange = (file) => {
    setTermsOfUse(file);
    if (file) setTermsOfUseSelection('custom');
    setIsFieldsTouched(Math.random());
  };

  const handleTermsOfUseDelete = () => {
    setTermsOfUse(null);
    setTermsOfUseSelection('default');
    setIsFieldsTouched(Math.random());
  };

  const handleTermsOfUseSelectionChange = (selection) => {
    setTermsOfUseSelection(selection);
    if (selection !== 'custom') setTermsOfUseValidationTriggered(false);
    setIsFieldsTouched(Math.random());
  };

  const getDocumentSchema = (title, description, defaultViewUrl) => ({
    allowedFileTypes: ALLOWED_FILE_TYPES.legal,
    title,
    description,
    maxFiles: 1,
    defaultViewUrl
  });

  const getPrivacyStatementStatus = () =>
    privacyStatementValidationTriggered && privacyStatementSelection === 'custom' && !privacyStatement
      ? 'error'
      : undefined;
  const getTermsOfUseStatus = () =>
    termsOfUseValidationTriggered && termsOfUseSelection === 'custom' && !termsOfUse ? 'error' : undefined;

  return (
    <>
      <OnboardingNavbar onLogout={handleSaveAndLogout} loading={updateConfigLoading} />
      <OnboardingProgress />
      <OnboardingContent>
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Datenschutzerklärung und Nutzungsbedingungen</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                oqio stellt Ihnen die Datenschutzerklärung und Nutzungsbedingungen zur Verfügung. Diese können Sie
                übernehmen oder eigene Dokumente hochladen, um Ihren Kunden den Zugang rechtssicher bereitzustellen.
                Beachten Sie bitte 5.9 der{' '}
                <a href="https://www.oqio.de" target="_blank" rel="noopener noreferrer">
                  AGB
                </a>{' '}
                von oqio.
              </OnboardingContentGroupText>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          <OnboardingContentGroup>
            <OnboardingDocumentSection>
              <InlineUploader
                value={privacyStatement}
                onChange={handlePrivacyStatementChange}
                onDeleteFiles={handlePrivacyStatementDelete}
                onSelectionChange={handlePrivacyStatementSelectionChange}
                schema={getDocumentSchema(
                  'Datenschutzerklärung',
                  'Datei im PDF Format. Maximale Dateigröße: 20 MB.',
                  config?.privacy_statement_default_url
                )}
                requireDeleteConfirmation={false}
                showFilesCount={false}
                status={getPrivacyStatementStatus()}
              />
            </OnboardingDocumentSection>
          </OnboardingContentGroup>

          <OnboardingContentGroup>
            <OnboardingDocumentSection>
              <InlineUploader
                value={termsOfUse}
                onChange={handleTermsOfUseChange}
                onDeleteFiles={handleTermsOfUseDelete}
                onSelectionChange={handleTermsOfUseSelectionChange}
                schema={getDocumentSchema(
                  'Nutzungsbedingungen',
                  'Datei im PDF Format. Maximale Dateigröße: 20 MB.',
                  config?.terms_of_use_default_url
                )}
                requireDeleteConfirmation={false}
                showFilesCount={false}
                status={getTermsOfUseStatus()}
              />
            </OnboardingDocumentSection>
          </OnboardingContentGroup>
        </OnboardingStepWrapper>
        <OnboardingActions onBack={handleOnBack} onNext={handleOnNext} loading={updateConfigLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  config: selectConfig(state),
  updateConfigLoading: selectUpdateConfigLoading(state)
});

const mapDispatchToProps = {
  updateConfig,
  updateConfigOnboardingSettings,
  logout
};

export const OnboardingStepDocuments = connect(mapStateToProps, mapDispatchToProps)(OnboardingStepDocumentsComponent);
